import React, { useState } from 'react';
import { Button, Form, Modal } from '@/components/ui';
import InfoTooltip from '../../../components/InfoTooltip';
import type { HeLineComment } from '../types';
import { HE_LINE_COMMENT_MAX } from '../utils/gridValidation';
import { formatDateTime } from '@/utils/displayDateTime';

function formatWhen(at: string | null | undefined): string {
  if (!at) return 'Not saved yet';
  return formatDateTime(at);
}

function byline(comment: HeLineComment): string {
  return `${comment.authorName?.trim() || 'You'} · ${formatWhen(comment.at)}`;
}

export interface LineCommentCellProps {
  lineNumber: number;
  comments: HeLineComment[] | undefined;
  /** Adding is allowed (submitter while entering, reviewer while reviewing). */
  canComment: boolean;
  onAdd: (text: string) => void;
}

/**
 * M23 – per-line comment column. Outlined icon when the line has no comment, filled when it has
 * one. Hover shows the latest comment with its author; click (or tap) opens the history with a box
 * to add one. Internal only – never on the vendor PDF.
 */
const LineCommentCell: React.FC<LineCommentCellProps> = ({ lineNumber, comments = [], canComment, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const latest = comments[comments.length - 1];
  const has = comments.length > 0;

  const openModal = () => {
    setDraft('');
    setOpen(true);
  };

  // A span, not a <button>: the read-only review grid sits in a disabled <fieldset>, which would
  // disable a button too, and comments must stay readable there.
  const button = (
    <span
      role="button"
      tabIndex={0}
      className={`tw-inline-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded ${
        has ? 'tw-text-primary' : 'tw-text-content-muted'
      } hover:tw-bg-surface-alt focus:tw-shadow-focus focus:tw-outline-none`}
      aria-label={has ? `Line ${lineNumber} comments (${comments.length})` : `Add a comment to line ${lineNumber}`}
      onClick={openModal}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModal();
        }
      }}
    >
      <i className={`bi ${has ? 'bi-chat-left-text-fill' : 'bi-chat-left'}`} aria-hidden="true" />
    </span>
  );

  const save = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd(text.slice(0, HE_LINE_COMMENT_MAX));
    setOpen(false);
  };

  return (
    <>
      {latest ? (
        <InfoTooltip
          id={`he-line-comment-${lineNumber}`}
          content={
            <span className="tw-block tw-max-w-xs tw-whitespace-pre-line">
              <span className="tw-mb-1 tw-block tw-text-xs tw-opacity-80">{byline(latest)}</span>
              {latest.text}
            </span>
          }
        >
          {button}
        </InfoTooltip>
      ) : (
        button
      )}

      <Modal show={open} onHide={() => setOpen(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Line {lineNumber} comments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {comments.length > 0 ? (
            <ul className="tw-mb-3 tw-list-none tw-space-y-2 tw-p-0">
              {comments.map((comment, index) => (
                <li key={`${comment.at ?? 'new'}-${index}`} className="tw-rounded tw-border tw-border-line tw-bg-surface-alt tw-p-2">
                  <p className="tw-mb-1 tw-text-xs tw-text-content-muted">{byline(comment)}</p>
                  <p className="tw-mb-0 tw-whitespace-pre-line tw-text-sm tw-text-content">{comment.text}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="tw-mb-3 tw-text-sm tw-text-content-muted">No comments on this line yet.</p>
          )}
          {canComment && (
            <Form.Group controlId={`he-line-comment-text-${lineNumber}`}>
              <Form.Label>Add a comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={draft}
                maxLength={HE_LINE_COMMENT_MAX}
                onChange={(e) => setDraft(e.target.value)}
              />
              <Form.Text>
                {draft.length}/{HE_LINE_COMMENT_MAX} – internal only, not included in the vendor PDF.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="light" onClick={() => setOpen(false)}>
            {canComment ? 'Cancel' : 'Close'}
          </Button>
          {canComment && (
            <Button type="button" variant="primary" disabled={!draft.trim()} onClick={save}>
              Save
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LineCommentCell;
